/**
 * melodyPreviewGeometry.ts — pure geometry for the pre-start melody preview (#11).
 *
 * Before singing (준비 단계) the console shows the whole target melody on a
 * pitch×time grid so the user can see what's coming. This pairs each note with
 * a label anchor computed by the SAME `buildScoreOverlay` used by the live view
 * (#8) — so once practice starts, the playhead and the user's pitch land on the
 * identical coordinate system (일관). Pure: no React/native, deterministic,
 * never throws — an empty melody degrades to no labels and a '—' range.
 */

import type { TargetMelody } from '@muzly/shared';

import { midiToSolfege } from './liveScoringView';
import { buildScoreOverlay } from './scoreOverlayGeometry';
import { midiToNoteName } from './scoringView';

import type { ScoreOverlay } from './scoreOverlayGeometry';

/** 한 목표 노트의 라벨 앵커(오버레이 좌표계, 노트 구간 중앙 위) + 표기. */
export type PreviewLabel = {
  x: number;
  y: number;
  noteName: string;
  solfege: string;
  lyric?: string;
};

export type MelodyPreview = {
  /** 라이브(#8)와 같은 좌표계 — 목표선/폭/높이/음역이 그대로 일치한다. */
  overlay: ScoreOverlay;
  labels: PreviewLabel[];
  lowMidi: number;
  highMidi: number;
  noteCount: number;
  /** 음역 요약(예: "C4~C5, 8음"). 노트가 없으면 '—'. */
  rangeLabel: string;
};

/**
 * 목표 멜로디를 미리보기 좌표 + 라벨 + 음역 요약으로. 순수/결정론, throw 안 함.
 */
export const buildMelodyPreview = (melody: TargetMelody): MelodyPreview => {
  const overlay = buildScoreOverlay({ melody, actual: [] });
  const labels: PreviewLabel[] = melody.notes.map((n, i) => {
    const line = overlay.targetLines[i];
    return {
      x: (line.x1 + line.x2) / 2,
      y: line.y1,
      noteName: midiToNoteName(n.midi),
      solfege: midiToSolfege(n.midi),
      lyric: n.lyric,
    };
  });
  const midis = melody.notes.map((n) => n.midi);
  const noteCount = melody.notes.length;
  const lowMidi = noteCount ? Math.min(...midis) : 0;
  const highMidi = noteCount ? Math.max(...midis) : 0;
  const rangeLabel = noteCount
    ? `${midiToNoteName(lowMidi)}~${midiToNoteName(highMidi)}, ${noteCount}음`
    : '—';

  return { overlay, labels, lowMidi, highMidi, noteCount, rangeLabel };
};
